import { FC, Suspense } from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';
import { Center } from '@chakra-ui/react';
import { Feature } from '@shinetools/pricing-plan-library';

import {
  FeatureFilterField,
  FeatureName,
  UserFilterValues,
} from '__generated__/GQL';
import { IS_PRODUCTION } from 'common/environment';
import useCanCompanyUserAccessFeature from 'common/hooks/useCanCompanyUserAccessFeature';
import { CompanyBankAccountsQueryProvider } from 'common/hooks/useCompanyBankAccounts';
import { useCurrentPricingPlan } from 'common/hooks/useCurrentPricingPlan';
import useFeatureFlagsSwitcher from 'common/hooks/useFeatureFlagsSwitcher';
import SunshineLoader from 'components/_core/SunshineLoader';
import * as bankRoutes from 'features/Bank/routes';
import * as bankTransferRequestsRoutes from 'features/BankTransferRequests/routes';
import CreateNewAccount from 'features/CreateNewAccount';
import EpAgreementWall from 'features/EpAgreementWall';
import * as expenseClaimRequestsRoutes from 'features/ExpenseClaimRequests/routes';
import {
  PostOnboardingAccountSetupContextProvider,
  usePostOnboardingAccountSetupContext,
} from 'features/PostOnboardingAccountSetup/context';
import * as referralRoutes from 'features/Referral/routes';
import AppLayout from 'layouts/AppLayout';

import * as routes from '../routes';

const WithinAppLayoutRoutes: FC<{
  isAccountLocked: boolean;
  isNewOnboardingAllowed: boolean;
  isLegalNumberRegistrationRequired: boolean;
  shouldAskForEpCguAgreement: boolean;
}> = ({
  isAccountLocked,
  isLegalNumberRegistrationRequired,
  isNewOnboardingAllowed,
  shouldAskForEpCguAgreement,
}) => {
  const { isFeatureAvailable } = useCurrentPricingPlan();
  const { featureFlags, isBetaTester } = useFeatureFlagsSwitcher();
  const { canAccessFeature } = useCanCompanyUserAccessFeature();

  const { hasCompletedPostOnboardingSetup } =
    usePostOnboardingAccountSetupContext();

  const canAccessAccountSetupRoute =
    canAccessFeature({ name: FeatureName.Activationonboarding }) &&
    !hasCompletedPostOnboardingSetup &&
    !isAccountLocked;

  const getFallbackRoute = () => {
    if (canAccessAccountSetupRoute) {
      return '/account-setup';
    }

    if (
      canAccessFeature({
        name: FeatureName.Bank,
        withChildFeatureOnly: true,
      })
    ) {
      return bankRoutes.root;
    }

    if (
      canAccessFeature({
        name: FeatureName.Accountingexport,
        withChildFeatureOnly: true,
      })
    ) {
      return '/accounting';
    }

    return '/company';
  };

  return (
    <PostOnboardingAccountSetupContextProvider>
      <CompanyBankAccountsQueryProvider>
        <AppLayout
          disableBrowsing={
            isLegalNumberRegistrationRequired ||
            isAccountLocked ||
            shouldAskForEpCguAgreement
          }
        >
          <Suspense
            fallback={
              <Center height="full">
                <SunshineLoader />
              </Center>
            }
          >
            {isAccountLocked ? (
              <routes.AccountLocked />
            ) : (
              <Switch>
                {/* Routes that should be accessible at any time */}
                <Route
                  component={routes.RecentActivities}
                  path="/recent-activities"
                />

                {canAccessAccountSetupRoute ? (
                  <Route
                    component={routes.PostOnboardingAccountSetup}
                    path="/account-setup"
                  />
                ) : null}

                {isNewOnboardingAllowed ? (
                  <Route
                    component={CreateNewAccount}
                    path="/create-new-account"
                  />
                ) : null}

                <Route component={routes.Help} path="/help" />

                {/* Routes that can block access to the rest */}
                {featureFlags.enableSiretWall ? (
                  <Route component={routes.SiretWall} path="/siret-wall" />
                ) : null}

                {isLegalNumberRegistrationRequired &&
                featureFlags.enableSiretWall ? (
                  <Redirect to="/siret-wall" />
                ) : null}

                {/* Don’t condition ep-agreement-wall route with shouldAskForEpCguAgreement,
                     otherwise users would be ejected from this flow as soon as it turns false.
                     And it’s not what we want because we show a success screen.
                    Instead, the component already handle its own access */}
                <Route component={EpAgreementWall} path="/ep-agreement-wall" />

                {shouldAskForEpCguAgreement ? (
                  <Redirect to="/ep-agreement-wall" />
                ) : null}

                {/* Rest of routes, that can be blocked */}
                {canAccessFeature({
                  name: FeatureName.Bank,
                  withChildFeatureOnly: true,
                }) ? (
                  <Route component={routes.Bank} path={bankRoutes.root} />
                ) : null}

                <Route component={routes.Profile} path="/profile" />

                {canAccessFeature({
                  name: FeatureName.CompanyProfile,
                  withChildFeatureOnly: true,
                }) ? (
                  <Route component={routes.Company} path="/company" />
                ) : null}

                {canAccessFeature({
                  name: FeatureName.Payments,
                  withChildFeatureOnly: true,
                }) ? (
                  <Route component={routes.Payments} path="/payments" />
                ) : null}

                {canAccessFeature({
                  name: FeatureName.RequestBanktransfers,
                  where: {
                    or: [
                      {
                        field: FeatureFilterField.User,
                        values: [UserFilterValues.Self],
                      },
                    ],
                  },
                }) ? (
                  <Route
                    component={routes.BankTransferRequests}
                    path={bankTransferRequestsRoutes.rootPath}
                  />
                ) : null}

                {canAccessFeature({
                  name: FeatureName.RequestExpenseclaims,
                  where: {
                    or: [
                      {
                        field: FeatureFilterField.User,
                        values: [UserFilterValues.Self],
                      },
                    ],
                  },
                }) ? (
                  <Route
                    component={routes.ExpenseClaimRequests}
                    path={expenseClaimRequestsRoutes.rootPath}
                  />
                ) : null}

                {canAccessFeature({
                  name: FeatureName.PaymentcardsRead,
                  withChildFeatureOnly: true,
                }) ? (
                  <Route component={routes.PaymentCards} path="/cards" />
                ) : null}

                <Route component={routes.Upgrade} path="/upgrade" />

                {canAccessFeature({
                  name: FeatureName.Invoicing,
                  withChildFeatureOnly: true,
                }) ? (
                  <Route component={routes.Work} path="/work" />
                ) : null}

                {canAccessFeature({
                  name: FeatureName.Insurance,
                  withChildFeatureOnly: true,
                }) ? (
                  <Route component={routes.Insurance} path="/insurances" />
                ) : null}

                {canAccessFeature({
                  name: FeatureName.Accountingexport,
                  withChildFeatureOnly: true,
                }) ? (
                  <Route component={routes.Accounting} path="/accounting" />
                ) : null}

                {!IS_PRODUCTION || isBetaTester ? (
                  <Route
                    component={routes.FeatureFlags}
                    path="/feature-flags"
                  />
                ) : null}

                {isFeatureAvailable(Feature.TeamManagement) &&
                canAccessFeature({
                  name: FeatureName.Team,
                  withChildFeatureOnly: true,
                }) ? (
                  <Route component={routes.Team} path="/team" />
                ) : null}

                {featureFlags.enableCashFlowManagement &&
                isFeatureAvailable(Feature.CashflowManagementDashboard) &&
                canAccessFeature({
                  name: FeatureName.Dashboard,
                  withChildFeatureOnly: true,
                }) ? (
                  <Route component={routes.Dashboard} path="/dashboard" />
                ) : null}

                {canAccessFeature({
                  name: FeatureName.Toolbox,
                  withChildFeatureOnly: true,
                }) ? (
                  <Route component={routes.Toolbox} path="/toolbox" />
                ) : null}

                {canAccessFeature({ name: FeatureName.Referral }) ? (
                  <Route
                    component={routes.ReferralRoutes}
                    path={referralRoutes.root}
                  />
                ) : null}

                {canAccessFeature({ name: FeatureName.MerchantRead }) &&
                featureFlags.enableNewPsp ? (
                  <Route component={routes.Merchant} path="/psp" />
                ) : null}

                <Route render={() => <Redirect to={getFallbackRoute()} />} />
              </Switch>
            )}
          </Suspense>
        </AppLayout>
      </CompanyBankAccountsQueryProvider>
    </PostOnboardingAccountSetupContextProvider>
  );
};

export default WithinAppLayoutRoutes;
